import { useState, useEffect, useCallback } from "react";

const CC_CONFIG = {
  clientId:    "adfa64c7-1bb9-4c2b-8987-632d68933a02",
  redirectUri: "https://portal.bio-ev.com",
  authUrl:     "https://authz.constantcontact.com/oauth2/default/v1/authorize",
  tokenUrl:    "https://bioev-portal-production.up.railway.app/oauth/token",
  apiBase:     "https://bioev-portal-production.up.railway.app/cc-api",
  scopes:      "contact_data",
};

function generateRandomString(length = 64) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join("");
}

async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

const TOKEN_KEY    = "cc_access_token";
const VERIFIER_KEY = "cc_pkce_verifier";
const STATE_KEY    = "cc_oauth_state";

function saveToken(token) { sessionStorage.setItem(TOKEN_KEY, token); }
function getToken()       { return sessionStorage.getItem(TOKEN_KEY); }
function clearToken()     {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
}

export async function startCCLogin() {
  const verifier  = generateRandomString(64);
  const challenge = await generateCodeChallenge(verifier);
  const state     = generateRandomString(16);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    response_type:         "code",
    client_id:             CC_CONFIG.clientId,
    redirect_uri:          CC_CONFIG.redirectUri,
    scope:                 CC_CONFIG.scopes,
    code_challenge:        challenge,
    code_challenge_method: "S256",
    state:                 state,
  });

  window.location.href = `${CC_CONFIG.authUrl}?${params.toString()}`;
}

export async function handleCCCallback() {
  const urlParams = new URLSearchParams(window.location.search);
  const code      = urlParams.get("code");
  const error     = urlParams.get("error");

  if (error) {
    console.error("CC OAuth error:", error, urlParams.get("error_description"));
    return null;
  }

  if (!code) return null;

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) {
    console.error("PKCE verifier missing — please try logging in again.");
    return null;
  }

  try {
    const response = await fetch(CC_CONFIG.tokenUrl, {
      method:  "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body:    new URLSearchParams({
        grant_type:    "authorization_code",
        client_id:     CC_CONFIG.clientId,
        redirect_uri:  CC_CONFIG.redirectUri,
        code,
        code_verifier: verifier,
      }),
    });

    if (!response.ok) {
      const err = await response.json();
      console.error("Token exchange failed:", err);
      return null;
    }

    const data = await response.json();
    saveToken(data.access_token);
    window.history.replaceState({}, document.title, window.location.pathname);
    return data.access_token;
  } catch (err) {
    console.error("Token exchange error:", err);
    return null;
  }
}

export async function fetchCCVendors(token) {
  try {
    const listsRes = await fetch(`${CC_CONFIG.apiBase}/contact_lists?limit=50`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (listsRes.status === 401) { clearToken(); return { error: "unauthorized", contacts: [] }; }
    if (!listsRes.ok)            { return { error: "api_error", contacts: [] }; }

    const listsData  = await listsRes.json();
    const vendorList = (listsData.lists || []).find(l => l.name === "Vendor List");

    if (!vendorList) {
      console.error("Could not find a list named 'Vendor List' in Constant Contact.");
      return { error: "list_not_found", contacts: [] };
    }

    let allContacts = [];
    let nextUrl = `${CC_CONFIG.apiBase}/contacts?status=active&limit=500&lists=${vendorList.list_id}`;

    while (nextUrl) {
      const response = await fetch(nextUrl, {
        headers: {
          Authorization:  `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) { clearToken(); return { error: "unauthorized", contacts: [] }; }
      if (!response.ok)            { return { error: "api_error", contacts: [] }; }

      const data  = await response.json();
      allContacts = [...allContacts, ...(data.contacts || [])];
      nextUrl     = data._links?.next?.href
        ? `${CC_CONFIG.apiBase}${data._links.next.href}`
        : null;
    }

    const vendors = allContacts.map((contact, index) => ({
      id:           contact.contact_id || `cc-${index}`,
      name:         `${contact.first_name || ""} ${contact.last_name || ""}`.trim(),
      company:      contact.company_name || "",
      email:        contact.email_address?.address || "",
      billingEmail: contact.email_address?.address || "",
      source:       "ConstantContact",
    }));

    return { error: null, contacts: vendors };
  } catch (err) {
    console.error("Fetch vendors error:", err);
    return { error: "network_error", contacts: [] };
  }
}

export function useCCVendors() {
  const [vendors,         setVendors]         = useState([]);
  const [isLoading,       setIsLoading]       = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error,           setError]           = useState(null);

  const loadVendors = useCallback(async (token) => {
    setIsLoading(true);
    const result = await fetchCCVendors(token);
    if (result.error === "unauthorized") {
      setIsAuthenticated(false);
      setVendors([]);
    } else if (result.error) {
      setError(result.error);
      setVendors([]);
    } else {
      setVendors(result.contacts);
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    async function init() {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("code")) {
        const token = await handleCCCallback();
        if (token) { await loadVendors(token); return; }
      }
      const existingToken = getToken();
      if (existingToken) { await loadVendors(existingToken); return; }
      setIsLoading(false);
      setIsAuthenticated(false);
    }
    init();
  }, [loadVendors]);

  const login  = () => startCCLogin();
  const logout = () => {
    clearToken();
    setIsAuthenticated(false);
    setVendors([]);
  };

  const refresh = useCallback(async () => { const token = getToken(); if (token) await loadVendors(token); }, [loadVendors]);
return { vendors, isLoading, isAuthenticated, error, login, logout, refresh };
}